import { FC, useEffect, useState } from 'react'
import { currencyFormat } from '../../../helpers'
import { Button, Checkbox, message, Pagination, Row, Select } from 'antd'
import { useSelector } from 'react-redux'
import style from './style.m.less'
import request from '../../../../services/request'
import IAppState from '../../../../store/states'
import { EUserRole } from '../../../../constants/approval'
import useRequest, { RequestTypes } from '../../../hooks/useRequest'
import { TranslationModal, MissingTranslation, LogoModal } from './components'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import {
    IRepaymentsBlock,
    LogoAvailability,
    LogoFlags,
    LogoNames,
    OptionType,
    OriginCertificateRequest,
    PaginationType,
} from './types'
import { AxiosResponseHeaders } from 'axios'
import { decodeFilename } from '../../../helpers'

type Locale = 'ru' | 'en'

const RepaymentsBlock: FC<IRepaymentsBlock> = ({ eventDetails, getCustomData, pagination_ }) => {
    const userInfo = useSelector((store: IAppState) => store.user)
    const isAdminOrOperator =
        userInfo.data.user_role === EUserRole.SuperAdmin ||
        userInfo.data.user_role === EUserRole.Administrator ||
        userInfo.data.user_role === EUserRole.Operator
    const showAllRepayments = isAdminOrOperator || userInfo.data.uid === eventDetails.uid_creator
    const [pagination] = useState<PaginationType>(pagination_)
    const [selectValue, setSelectValue] = useState<number | null>(null)
    const disabledButton = selectValue == null

    const [translationModalVisible, setTranslationModalVisible] = useState(false)
    const [missingTranslations, setMissingTranslations] = useState<MissingTranslation[]>([])

    const { fetch: fetchTranslate } = useRequest({
        link: '/api/extension/v1/extension/certificates/translates/',
        method: RequestTypes.post,
    })
    const { fetch: fetchUserType, result: resUserType } = useRequest({
        link: 'api/extension/v1/extension/origin_certificates/user-type/',
        method: RequestTypes.get,
    })

    const [useLogos, setUseLogos] = useState<boolean>(false)
    const [isNewFormat, setIsNewFormat] = useState<boolean>(false)
    const [isGradient, setIsGradient] = useState<boolean>(false)
    const [isA3, setIsA3] = useState<boolean>(false)

    const disabledIsA3 = disabledButton || !isNewFormat || resUserType?.u_type !== 'ul'

    const [logoModalVisible, setLogoModalVisible] = useState<boolean>(false)
    const [logoNames, setLogoNames] = useState<LogoNames>()
    const [logoAvailability, setLogoAvailability] = useState<LogoAvailability>()
    const [pendingLocale, setPendingLocale] = useState<Locale | null>(null)

    const getList = () => {
        if (showAllRepayments) {
            return eventDetails.payload.repayments
        } else {
            const data = []
            for (const repayment of eventDetails.payload.repayments) {
                if (repayment.user_id === userInfo.data.uid) {
                    data.push(repayment)
                }
            }
            return data
        }
    }
    const getTotal = () => {
        if (showAllRepayments) {
            return eventDetails.payload.total
        } else {
            let data = 0
            for (const repayment of eventDetails.payload.repayments) {
                if (repayment.user_id === userInfo.data.uid) {
                    data++
                }
            }
            return data
        }
    }
    const list = getList()
    const total = getTotal()

    const getData = (): OptionType[] => {
        const data: OptionType[] = []
        if (showAllRepayments) {
            for (const repayment of eventDetails.payload.repayments) {
                data.push({ value: repayment.idx, label: repayment.user_name })
            }
        } else {
            for (const repayment of eventDetails.payload.repayments) {
                if (repayment.user_id === userInfo.data.uid) {
                    data.push({ value: repayment.idx, label: repayment.user_name })
                }
            }
        }
        return data
    }
    const selectChange = (value: number) => {
        setSelectValue(value)
        resetCheckboxes()

        const repayment = eventDetails.payload.repayments.find((r: any) => r.idx === value)
        fetchUserType({ addToLink: repayment.user_id })
    }

    useEffect(() => {
        if (!missingTranslations.length) {
            setTranslationModalVisible(false)
        }
    }, [missingTranslations])

    async function handleRequestError(err: any, locale: string) {
        if (err instanceof Blob) {
            const res = JSON.parse(await err.text())
            if (res?.detail?.user_errors?.length > 0 && locale === 'en') {
                try {
                    const text_data: string[] = await res.detail.user_errors
                    if (text_data) {
                        setMissingTranslations(text_data.map((text) => ({ text })))
                        setTranslationModalVisible(true)
                    }
                } catch (parseErr) {
                    console.error('Ошибка при парсинге списка строк без переводов:', parseErr)
                }
            } else if (res?.detail?.operator_errors?.length > 0 && locale === 'en') {
                message.error(
                    'Данных для формирования Свидетельства на английском языке недостаточно' +
                        ', обратитесь к оператору с использованием раздела "Мои заявки"',
                )
            } else if (res?.detail === 'Макет A3 для сертификата происхождения недоступен') {
                message.error(
                    'Сертификат происхождения в формате А3 доступен только для юридических лиц',
                )
            }
        }
    }

    async function downloadCertificate(locale: Locale, flags?: LogoFlags) {
        const repayment = eventDetails.payload.repayments.find(
            (repayment: any) => repayment.idx === selectValue,
        )
        if (!repayment) {
            return
        }

        const { certificate_id, generator_id, owner_generator_id } = eventDetails.payload
        const { user_id, user_name, volume, account_number, idx } = repayment

        try {
            const params: OriginCertificateRequest = {
                user_id,
                account_number,
                volume,
                generator_id,
                owner_generator_id,
                certificate_id,
                client_name: user_name,
                idx,
                ts_event: eventDetails.ts_event,
                locale,
                is_gradient: isGradient,
                is_a3: isA3,
            }

            if (flags) {
                params.client_logo = flags.client_logo
                params.owner_logo = flags.owner_logo
                params.go_logo = flags.go_logo
                if (flags.main_logo_id) {
                    params.main_logo_id = flags.main_logo_id
                }
            }

            await downloadPDF(params)
        } catch (err: any) {
            await handleRequestError(err, locale)
        }
    }

    async function collectPdfErrors(
        locale: 'en' | 'ru',
    ): Promise<{ user: boolean; operator: boolean }> {
        const repayment = eventDetails.payload.repayments.find(
            (repayment: any) => repayment.idx === selectValue,
        )
        if (!repayment) {
            return { user: true, operator: true }
        }
        const { certificate_id, generator_id, owner_generator_id } = eventDetails.payload
        const { user_id, user_name, volume, account_number, idx } = repayment

        try {
            const params: OriginCertificateRequest = {
                user_id,
                account_number,
                volume,
                generator_id,
                owner_generator_id,
                certificate_id,
                client_name: user_name,
                idx,
                ts_event: eventDetails.ts_event,
                locale,
            }
            await sendPDFRequest(params)
            return { user: false, operator: false }
        } catch (err: any) {
            let userErrors = false
            let operatorErrors = false
            if (err instanceof Blob) {
                const res = JSON.parse(await err.text())
                userErrors = !!(res?.detail?.user_errors?.length && locale === 'en')
                operatorErrors = !!(res?.detail?.operator_errors?.length && locale === 'en')
            }
            await handleRequestError(err, locale)
            return { user: userErrors, operator: operatorErrors }
        }
    }

    async function sendPDFRequest(
        params: OriginCertificateRequest,
    ): Promise<{ data: any; headers: AxiosResponseHeaders }> {
        const requestUrl = isNewFormat
            ? '/api/extension/v1/files/certificates/origin/pdf/side_layout'
            : '/api/extension/v1/files/certificates/origin/pdf/top_layout'
        return await request.get(requestUrl, {
            params,
            responseType: 'blob',
        })
    }

    async function downloadPDF(params: OriginCertificateRequest): Promise<void> {
        const { data: pdfFile, headers: pdfHeaders } = await sendPDFRequest(params)

        const fileRef = document.createElement('a')
        const filename = pdfHeaders && (pdfHeaders as any)['filename']
        fileRef.href = URL.createObjectURL(new Blob([pdfFile], { type: 'application/pdf' }))
        fileRef.download = decodeFilename(filename)
        fileRef.target = '_blank'
        fileRef.click()
        URL.revokeObjectURL(fileRef.href)
        fileRef.remove()
    }

    async function downloadArchive() {
        const { certificate_id, generator_id, owner_generator_id } = eventDetails.payload
        const downloadPromises = []

        for (const repayment of eventDetails.payload.repayments) {
            if (
                showAllRepayments ||
                (!showAllRepayments && repayment.user_id === userInfo.data.uid)
            ) {
                const { user_id, user_name, volume, account_number, idx } = repayment
                const params: OriginCertificateRequest = {
                    user_id: user_id,
                    account_number: account_number,
                    volume: volume,
                    generator_id: generator_id,
                    owner_generator_id: owner_generator_id,
                    certificate_id: certificate_id,
                    client_name: user_name,
                    idx: idx,
                    ts_event: eventDetails.ts_event,
                    locale: 'ru',
                    is_gradient: false,
                }

                downloadPromises.push(downloadPDF(params))
            }
        }

        await Promise.all(downloadPromises)
    }

    const onSubmitTranslate = (ru: string, en: string, idx: number) => {
        fetchTranslate({
            body: { ru, en },
            onSuccess: () => {
                message.success('Перевод успешно отправлен')
                setMissingTranslations((prev) => prev.filter((_, i) => i !== idx))
            },
            onError: () => {
                message.error('Ошибка при отправке перевода')
            },
        })
    }

    async function prepareLogoModalData(locale: Locale) {
        const repayment = eventDetails.payload.repayments.find((r: any) => r.idx === selectValue)
        if (!repayment) {
            return
        }

        try {
            const [availability_request, names_request] = await Promise.all([
                request.get('/api/extension/v1/extension/certificates/logos/available/', {
                    params: {
                        client_id: repayment.user_id,
                        generator_id: eventDetails.payload.generator_id,
                        owner_id: eventDetails.payload.owner_generator_id,
                    },
                }),
                request.get('/api/extension/v1/extension/certificates/logos/owners/', {
                    params: {
                        client_id: repayment.user_id,
                        generator_id: eventDetails.payload.generator_id,
                        owner_id: eventDetails.payload.owner_generator_id,
                    },
                }),
            ])

            const names = names_request.data
            const availability = availability_request.data

            setLogoNames({
                client_name: names.client_name,
                go_name: names.go_name,
                owner_name: names?.owner_name,
                client_type: names?.client_type,

                client_id: repayment.user_id,
                generator_id: eventDetails.payload.generator_id,
                owner_id: eventDetails.payload.owner_generator_id,
            })
            setLogoAvailability({
                client_available: availability.client_available,
                go_available: availability.go_available,
                owner_available: availability.owner_available,
            })
            setPendingLocale(locale)
            setLogoModalVisible(true)
        } catch {
            message.error('Не удалось получить данные по логотипам')
        }
    }

    async function handleDownloadWithLogos(locale: Locale) {
        if (selectValue == null) {
            return
        }

        if (!useLogos) {
            return downloadCertificate(locale)
        }

        if (locale === 'ru') {
            return await prepareLogoModalData(locale)
        }

        if (locale === 'en') {
            const { user: userErrors, operator: operatorErrors } = await collectPdfErrors(locale)
            if (userErrors || operatorErrors) {
                return
            }
            return await prepareLogoModalData(locale)
        }
    }

    const handleLogoSubmit = async (flags: LogoFlags) => {
        if (!pendingLocale) {
            return
        }
        await downloadCertificate(pendingLocale, flags)
        setLogoModalVisible(false)
        setPendingLocale(null)
    }

    function resetCheckboxes() {
        setIsA3(false)
        setIsGradient(false)
        setIsNewFormat(false)
        setUseLogos(false)
    }

    return (
        <>
            <div className={style.energyTableWrap}>
                <div className={style.energyBlock}>
                    <div className={style.energyHeader}>{`Потребители СП (${total})`}</div>
                    <div className={style.energyWrapper}>
                        <div className={style.energyTop}>Потребитель</div>
                        <div className={style.energyTop}>Объем, кВт*ч</div>
                    </div>
                    {list?.map((item: any, index: number) => {
                        return (
                            <div
                                key={index}
                                className={
                                    index === list.length - 1
                                        ? style.energyItemWrapperBottom
                                        : style.energyItemWrapper
                                }
                            >
                                <div className={style.energyItem}>{item.user_name}</div>
                                <div className={style.energyItem}>
                                    {currencyFormat(item.volume)}
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className={style.dropdownWrap}>
                    <div>
                        <div className={style.dropdownLabel}>
                            Потребитель СП для свидетельства о погашении СП
                        </div>
                        <Select<number, OptionType>
                            showSearch
                            placeholder="Выберите потребителя"
                            className={style.dropdownMenu}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            onChange={selectChange}
                            options={getData()}
                            value={selectValue ?? undefined}
                        />
                    </div>
                    <Row>
                        <Checkbox
                            checked={isNewFormat}
                            onChange={(e: CheckboxChangeEvent) => {
                                setIsNewFormat(e.target.checked)
                                setIsA3(false)
                            }}
                            disabled={disabledButton}
                            style={{ flexDirection: 'row-reverse' }}
                        >
                            Новый формат
                        </Checkbox>
                        <Checkbox
                            checked={isGradient}
                            onChange={(e: CheckboxChangeEvent) => setIsGradient(e.target.checked)}
                            disabled={disabledButton}
                            style={{ flexDirection: 'row-reverse' }}
                        >
                            Для цветной печати
                        </Checkbox>
                        <Checkbox
                            checked={useLogos}
                            onChange={(e: CheckboxChangeEvent) => setUseLogos(e.target.checked)}
                            disabled={disabledButton}
                            style={{ flexDirection: 'row-reverse' }}
                        >
                            Логотипы
                        </Checkbox>
                        <Checkbox
                            checked={isA3}
                            onChange={(e: CheckboxChangeEvent) => setIsA3(e.target.checked)}
                            disabled={disabledIsA3}
                            style={{ flexDirection: 'row-reverse' }}
                        >
                            А3
                        </Checkbox>
                    </Row>
                    <div>
                        <Button
                            disabled={disabledButton}
                            onClick={() => handleDownloadWithLogos('ru')}
                            style={{ width: 330 }}
                        >
                            Выгрузить свидетельство о погашении СП (ru)
                        </Button>
                    </div>
                    <div>
                        <Button
                            disabled={disabledButton}
                            onClick={() => handleDownloadWithLogos('en')}
                            style={{ width: 330 }}
                        >
                            Выгрузить свидетельство о погашении СП (eng)
                        </Button>
                    </div>
                    <div>
                        <Button onClick={downloadArchive}>Выгрузить все</Button>
                    </div>
                </div>
            </div>
            {total > 10 && (
                <Pagination
                    className={style.pagination}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    total={total || 0}
                    onChange={(page, pageSize) => {
                        getCustomData({ page, pageSize })
                        //                        setPagination({ page, pageSize })
                    }}
                    showSizeChanger
                />
            )}
            <TranslationModal
                visible={translationModalVisible}
                items={missingTranslations}
                onClose={() => {
                    setTranslationModalVisible(false)
                    setMissingTranslations([])
                }}
                onSubmit={onSubmitTranslate}
            />
            <LogoModal
                visible={logoModalVisible}
                names={logoNames}
                availability={logoAvailability}
                onClose={() => {
                    setLogoModalVisible(false)
                    setPendingLocale(null)
                }}
                onSubmit={handleLogoSubmit}
                isNewFormat={isNewFormat}
            />
        </>
    )
}

export default RepaymentsBlock
